import { TERMS as BASE_TERMS, PRIVACY as BASE_PRIVACY, AI_POLICY as BASE_AI_POLICY } from '@gamecodex/portal-ui'
import type { LegalDoc, LegalSection, LegalDocKey } from '@gamecodex/portal-ui'

/**
 * DiceTok overrides the storage-related sections of the shared portal-ui legal
 * content (packages/@gamecodex/portal-ui/src/legal/docs.ts). That shared copy
 * is written around a guarantee true of most portals in this family — "nothing
 * you submit is stored" — which is false for DiceTok on purpose: ClippyJack's
 * submissions ARE kept, tied to the poster's account, so a human can pull
 * genuinely funny ones for the show later (see netlify/functions/clip.ts and
 * atlas's /dicetok/submissions). The shared module anticipates exactly this
 * case: override the relevant doc rather than editing the shared copy in place.
 *
 * ⚠️ The override matches on section HEADINGS in the shared document. A rename
 * upstream would otherwise make an override silently inert and ship the wrong
 * ("not stored") claim on the one portal that stores everything — which is not
 * a hypothetical, it happened when two legal modules were merged into one.
 * `replaceSection` therefore THROWS on a missed match rather than returning the
 * document unchanged. A loud build failure is the cheap version of this bug.
 */

const UPDATED = 'July 25, 2026'

/** Swap the section titled `heading`. Throws if it is not there. */
function replaceSection(doc: LegalDoc, heading: string, replacement: LegalSection): LegalSection[] {
  const sections = doc.sections ?? []
  if (!sections.some((s) => s.heading === heading)) {
    throw new Error(
      `DiceTok legal override targets "${heading}" in "${doc.title}", which no longer exists upstream. ` +
        `Available: ${sections.map((s) => s.heading).join(' | ')}`,
    )
  }
  return sections.map((s) => (s.heading === heading ? replacement : s))
}

const DICETOK_SUBMIT_SECTION: LegalSection = {
  heading: '5. Content You Submit',
  body: [
    'Posting to {portal} requires a signed-in account. When you submit a "moment," it is sent to a third-party AI model to generate ClippyJack’s in-character clip response — and, unlike most portals in this family, that submission and the generated response ARE stored afterward, tied to your account. See our Privacy Policy for retention detail and how to request deletion.',
    'We keep it because a human may later review stored submissions to decide whether to feature a genuinely funny one in the show or its promotional content. That review is separate from, and happens after, the automatic AI response you see immediately — it is not guaranteed for any given post.',
    'You keep ownership of what you write. By submitting a moment, you grant {entity} a non-exclusive, worldwide, royalty-free license to store, reproduce, and publicly display your submission and the generated response for operating the Service and for the featuring described above. Don’t submit anything you lack the rights to submit, or personal information about other people.',
  ],
}

const DICETOK_COLLECT_SECTION: LegalSection = {
  heading: '1. What We Collect',
  body: [
    'Account information: if you register or log in, {entity} Accounts collects your email address, a securely hashed password, an optional display name, and basic login metadata (timestamp, IP address, device/browser identifiers) used for security and fraud prevention.',
    'Session data: after you log in, an access token and a refresh token are stored in your browser’s local storage so you stay signed in. These identify your session, not you personally beyond your account.',
    'Submissions: unlike most portals in this family, {portal} stores what you submit — the "moment" text, any caption, and the AI-generated verdict — tied to your account. See "Content You Submit, and AI Processing" below.',
  ],
}

const DICETOK_PROCESS_SECTION: LegalSection = {
  heading: '5. Why We Process It',
  body: [
    'We use account information to operate the Service: authenticating you, keeping your account secure, and preventing abuse. We do not use your account information for advertising, and we do not sell personal information.',
    'Text you submit is used to generate ClippyJack’s response and is sent to our AI subprocessor for that purpose, under its own data-handling terms; it does not use it to train its own models. We also store the submission and the response, tied to your account, so our team can review it afterward for possible featuring in the show — see "Content You Submit" in our Terms. We do not use stored submissions to train any of our own models either.',
  ],
}

const DICETOK_RETENTION_SECTION: LegalSection = {
  heading: '7. Retention',
  body: [
    'We retain account information for as long as your account is active, plus a reasonable period afterward for security, fraud-prevention, and legal-compliance purposes. Submissions and their generated responses are retained indefinitely, tied to your account, unless you request deletion — contact privacy@gamecodex.com to have a submission or your account data removed.',
  ],
}

/**
 * The shared §4 is written conditionally ("where a portal states that
 * submissions are not stored…"), which is not false for DiceTok but does leave
 * a reader to work out which side of the condition they are on. Say it plainly.
 */
const DICETOK_AI_PROCESSING_SECTION: LegalSection = {
  heading: '4. Content You Submit, and AI Processing',
  body: [
    'What you post is sent to a third-party AI provider to generate ClippyJack’s response. That transmission is necessary to provide the feature.',
    '{portal} STORES what you submit and the response it generated, tied to your account. This is the opposite of most portals in this family, and it is deliberate: a human may review stored submissions to decide whether to feature one in the show.',
    'Our AI provider processes the text to return a response and does not use it to train its models. It may retain it briefly for abuse monitoring under its own policies.',
    'Don’t submit sensitive personal information, credentials, other people’s personal information, or anything you would not want kept and possibly read by a person later.',
  ],
}

export const TERMS: LegalDoc = {
  ...BASE_TERMS,
  updated: UPDATED,
  sections: replaceSection(BASE_TERMS, '5. Content You Submit', DICETOK_SUBMIT_SECTION),
}

export const PRIVACY: LegalDoc = {
  ...BASE_PRIVACY,
  updated: UPDATED,
  sections: [
    ['1. What We Collect', DICETOK_COLLECT_SECTION],
    ['4. Content You Submit, and AI Processing', DICETOK_AI_PROCESSING_SECTION],
    ['5. Why We Process It', DICETOK_PROCESS_SECTION],
    ['7. Retention', DICETOK_RETENTION_SECTION],
  ].reduce<LegalDoc>(
    (doc, [heading, replacement]) => ({
      ...doc,
      sections: replaceSection(doc, heading as string, replacement as LegalSection),
    }),
    BASE_PRIVACY,
  ).sections!,
}

/**
 * The shared AI policy is prose, not sections, so this appends rather than
 * replaces — the shared paragraphs stay true for DiceTok, they are just silent
 * on the fact that we keep what you post.
 */
const DICETOK_AI_PARAGRAPH =
  'One thing specific to {portal}: what you post, and the response ClippyJack gives it, are stored and tied to your account, so a person can look through them later and pick out a good one. Most portals in this family discard what you submit. This one does not. Don’t post anything you wouldn’t want kept.'

export const AI_POLICY: LegalDoc = {
  ...BASE_AI_POLICY,
  updated: UPDATED,
  body: [...(Array.isArray(BASE_AI_POLICY.body) ? BASE_AI_POLICY.body : [BASE_AI_POLICY.body ?? '']), DICETOK_AI_PARAGRAPH],
}

export const LEGAL_DOCS: Record<LegalDocKey, LegalDoc> = {
  terms: TERMS,
  privacy: PRIVACY,
  ai: AI_POLICY,
}
